import { io, Socket } from 'socket.io-client';
import type { Message } from '../types';

const WS_URL = import.meta.env.VITE_WS_URL || '';

export class WebSocketClient {
  private socket: Socket | null = null;

  connect(token?: string, anonymousUserId?: string, anonymousName?: string): void {
    if (this.socket?.connected) {
      return;
    }

    const wsUrl = WS_URL || window.location.origin;
    const auth: any = {};
    
    if (token) {
      auth.token = token;
    } else if (anonymousUserId && anonymousName) {
      auth.anonymousUserId = anonymousUserId;
      auth.anonymousName = anonymousName;
    }
    // If neither provided, server will create anonymous user

    this.socket = io(wsUrl, {
      path: '/ws',
      auth,
      transports: ['websocket', 'polling'],
    });

    this.socket.on('connect', () => {
      console.log('WebSocket connected');
    });

    this.socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
    });

    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error);
      if (error instanceof Error) {
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
      } else if (typeof error === 'object') {
        console.error('Error details:', JSON.stringify(error, null, 2));
      }
    });
    
    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error.message || error);
    });
  }

  updateAnonymousName(name: string): void {
    if (this.socket) {
      this.socket.emit('update-anonymous-name', { name });
    }
  }

  onAnonymousNameUpdated(callback: (data: { name: string }) => void): void {
    if (this.socket) {
      this.socket.on('anonymous-name-updated', callback);
    }
  }

  offAnonymousNameUpdated(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('anonymous-name-updated', callback);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  joinRoom(roomId: string): void {
    if (this.socket && this.socket.connected) {
      console.log('Joining room:', roomId);
      this.socket.emit('join-room', roomId);
    } else {
      console.warn('Cannot join room: socket not connected', {
        socketExists: !!this.socket,
        connected: this.socket?.connected
      });
    }
  }

  leaveRoom(roomId: string): void {
    if (this.socket) {
      this.socket.emit('leave-room', roomId);
    }
  }

  sendMessage(roomId: string, content: string): void {
    if (this.socket && this.socket.connected) {
      console.log('Emitting send-message:', { roomId, content });
      this.socket.emit('send-message', { roomId, content });
    } else {
      console.error('Cannot send message: socket not connected', {
        socketExists: !!this.socket,
        connected: this.socket?.connected
      });
    }
  }

  onMessage(callback: (message: Message) => void): void {
    if (this.socket) {
      this.socket.on('new-message', callback);
      console.log('Message listener registered');
    } else {
      console.warn('Cannot register message listener: socket not available');
    }
  }

  offMessage(callback: (message: Message) => void): void {
    if (this.socket) {
      this.socket.off('new-message', callback);
    }
  }

  onUserJoined(callback: (data: { userId: string; username: string }) => void): void {
    if (this.socket) {
      this.socket.on('user-joined', callback);
    }
  }

  onUserLeft(callback: (data: { userId: string }) => void): void {
    if (this.socket) {
      this.socket.on('user-left', callback);
    }
  }

  offUserJoined(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('user-joined', callback);
    }
  }

  offUserLeft(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('user-left', callback);
    }
  }

  onTyping(callback: (data: { userId: string; username: string; isTyping: boolean }) => void): void {
    if (this.socket) {
      this.socket.on('typing', callback);
    }
  }

  emitTyping(roomId: string, isTyping: boolean): void {
    if (this.socket) {
      this.socket.emit('typing', { roomId, isTyping });
    }
  }

  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  // WebRTC Signaling Methods
  requestCall(targetUserId: string, roomId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-call-request', { targetUserId, roomId });
    }
  }

  acceptCall(callId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-call-accept', { callId });
    }
  }

  rejectCall(callId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-call-reject', { callId });
    }
  }

  sendOffer(callId: string, offer: RTCSessionDescriptionInit, targetUserId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-offer', { callId, offer, targetUserId });
    }
  }

  sendAnswer(callId: string, answer: RTCSessionDescriptionInit, targetUserId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-answer', { callId, answer, targetUserId });
    }
  }

  sendIceCandidate(callId: string, candidate: RTCIceCandidateInit, targetUserId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-ice-candidate', { callId, candidate, targetUserId });
    }
  }

  endCall(callId: string): void {
    if (this.socket) {
      this.socket.emit('webrtc-call-end', { callId });
    }
  }

  // WebRTC Event Listeners
  onCallRequest(callback: (data: { callId: string; callerId: string; callerUsername: string; roomId: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-call-request', callback);
    }
  }

  offCallRequest(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-call-request', callback);
    }
  }

  onCallInitiated(callback: (data: { callId: string; targetUserId: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-call-initiated', callback);
    }
  }

  offCallInitiated(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-call-initiated', callback);
    }
  }

  onCallAccepted(callback: (data: { callId: string; calleeId: string; calleeUsername: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-call-accepted', callback);
    }
  }

  offCallAccepted(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-call-accepted', callback);
    }
  }

  onCallRejected(callback: (data: { callId: string; calleeId: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-call-rejected', callback);
    }
  }

  offCallRejected(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-call-rejected', callback);
    }
  }

  onCallEnded(callback: (data: { callId: string; endedBy: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-call-ended', callback);
    }
  }

  offCallEnded(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-call-ended', callback);
    }
  }

  onWebRTCOffer(callback: (data: { callId: string; offer: RTCSessionDescriptionInit; fromUserId: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-offer', callback);
    }
  }

  offWebRTCOffer(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-offer', callback);
    }
  }

  onWebRTCAnswer(callback: (data: { callId: string; answer: RTCSessionDescriptionInit; fromUserId: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-answer', callback);
    }
  }

  offWebRTCAnswer(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-answer', callback);
    }
  }

  onWebRTCIceCandidate(callback: (data: { callId: string; candidate: RTCIceCandidateInit; fromUserId: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-ice-candidate', callback);
    }
  }

  offWebRTCIceCandidate(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-ice-candidate', callback);
    }
  }

  onCallError(callback: (data: { message: string }) => void): void {
    if (this.socket) {
      this.socket.on('webrtc-call-error', callback);
    }
  }

  offCallError(callback: (data: any) => void): void {
    if (this.socket) {
      this.socket.off('webrtc-call-error', callback);
    }
  }
}

export const wsClient = new WebSocketClient();

